import { PencilIcon, PlusCircleIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiFetcher } from '@/data/fetcher'
import { VariantAxisFormModal } from '@/forms/VariantAxisForm/VariantAxisFormModal'
import { VariantAxis } from '@/interfaces/catalog/VariantAxis'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

export const VariantAxes = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog',
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAxis, setEditingAxis] = useState<VariantAxis | null>(null)
  const [deletingAxis, setDeletingAxis] = useState<VariantAxis | null>(null)

  const {
    data: response,
    isLoading,
    mutate,
  } = useSWR<{ data?: VariantAxis[]; total?: number }>(
    '/api/v2/variant-axes',
    apiFetcher,
  )

  const axes = response?.data || []

  const openModal = (axis: VariantAxis | null) => {
    setEditingAxis(axis)
    setIsModalOpen(true)
  }

  const onDelete = async () => {
    if (!deletingAxis) return
    try {
      await API.delete(`/api/v2/variant-axes/${deletingAxis.id}`)
      toast.success(t('variantAxes.delete.success'))
      mutate()
    } catch (error: unknown) {
      toast.error(t('variantAxes.delete.error'), {
        description: extractErrorMessage(error) || undefined,
      })
    }
    setDeletingAxis(null)
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button onClick={() => openModal(null)}>
            <PlusCircleIcon className="size-5" />
            {t('variantAxes.actions.create')}
          </Button>
        </div>

        <div className="border rounded-lg p-4">
          {axes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('variantAxes.empty')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('variantAxes.table.name')}</TableHead>
                  <TableHead>{t('variantAxes.table.code')}</TableHead>
                  <TableHead>{t('variantAxes.table.valueType')}</TableHead>
                  <TableHead>{t('variantAxes.table.values')}</TableHead>
                  <TableHead className="text-right">{t('variantAxes.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {axes.map((axis) => (
                  <TableRow key={axis.id}>
                    <TableCell>{axis.name}</TableCell>
                    <TableCell>{axis.code}</TableCell>
                    <TableCell>
                      {t(`variantAxes.form.valueType.options.${axis.valueType}`)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {axis.values.map((value) => (
                          <Badge key={value.id} variant="secondary">
                            {value.label}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        title={t('variantAxes.actions.edit')}
                        onClick={() => openModal(axis)}
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={t('variantAxes.actions.delete')}
                        onClick={() => setDeletingAxis(axis)}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <VariantAxisFormModal
        isOpen={isModalOpen}
        axis={editingAxis}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => mutate()}
      />

      <AlertDialog
        open={!!deletingAxis}
        onOpenChange={(open) => !open && setDeletingAxis(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('variantAxes.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('variantAxes.delete.description', { name: deletingAxis?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('variantAxes.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete}>
              {t('variantAxes.delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
